import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FileText, LucideAngularModule, X } from 'lucide-angular';
import { Contract, Loa, LoaBillConfig, LoaSchedule, Vendor } from '../../../../core/models/domain.models';
import { ApiService } from '../../../../core/services/api.service';
import { ContractsService } from '../../../contract/services/contracts.service';
import { LoaService } from '../../../loa/services/loa.service';
import { VendorsService } from '../../../vendor/services/vendors.service';

type ContractSupportRowApi = {
  contract_id: string;
  contract_number: string;
  contract_type: string;
  contract_category: string | null;
  issue_date: string;
  period_from: string;
  period_to: string;
  status: string;
  bess_vendor_id: string;
  bess_vendor_name: string;
  bess_role: string;
  counterparty_vendor_id: string;
  counterparty_vendor_name: string;
  quantum: number | null;
  agreement_type: string | null;
  energy_flow: string;
  loa_id?: string | null;
  rate_type?: string | null;
  loa_fixed_rate?: number | null;
  loa_mcp_value?: number | null;
};

type ApplicationApprovalScheduleApi = {
  schedule_from: string;
  schedule_to?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  mwh?: number | null;
  requested_mw?: number | null;
  accepted_mw?: number | null;
  is_approved?: boolean;
};

type ApplicationChargeApi = {
  group_name: string;
  section_heading?: string | null;
  sub_group_name: string;
  rate_mwh?: number | null;
  quantum_mwh?: number | null;
  amount?: number | null;
};

type ApplicationApi = {
  id: string;
  application_number: string;
  contract_id?: string | null;
  application_type?: string | null;
  application_date: string;
  status: string;
  seller_loa_id?: string | null;
  buyer_loa_id?: string | null;
  approval_schedules?: ApplicationApprovalScheduleApi[];
  approval_charges?: ApplicationChargeApi[];
};

type GeneratedInvoiceApi = {
  row_key: string;
  bill_id: string;
  mode?: string | null;
  invoice_type?: string | null;
  bill_for?: string | null;
  invoice_date?: string | null;
  row_json?: Record<string, unknown> | null;
};

type OaChargeAllocationRow = {
  chargeType: string;
  paidBy: 'Buyer' | 'Seller' | 'Both';
  buyerPercent: number;
  sellerPercent: number;
};

type ChargeAllocationResult = {
  totalCharges: number;
  seciAmount: number;
  buyerAmount: number;
  sellerAmount: number;
  seciSide: 'buyer' | 'seller';
  rows: Array<{
    applicationNumber: string;
    chargeType: string;
    totalAmount: number;
    buyerAmount: number;
    sellerAmount: number;
    seciAmount: number;
    allocationLabel: string;
  }>;
};

type ReportMonthOption = {
  value: string;
  label: string;
};

type ProfitLossReportRow = {
  serialNo: number;
  clientName: string;
  contractNumber: string;
  buyer: string;
  seller: string;
  buyerLoa: string;
  sellerLoa: string;
  buyCost: number;
  sellCost: number;
  charges: number;
  totalRevenue: number;
  billed: number;
  netProfit: number;
};

type CalculationModalData = {
  title: string;
  subtitle: string;
  equation: string;
  hint: string;
  details: Array<{ label: string; value: string }>;
};

@Component({
  standalone: true,
  selector: 'app-profit-loss-report-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './profit-loss-report.page.html',
  styleUrl: './profit-loss-report.page.scss',
})
export class ProfitLossReportPage {
  readonly FileTextIcon = FileText;
  readonly CloseIcon = X;

  readonly profitLossMonthOptions: ReportMonthOption[] = [
    { value: '01', label: 'Jan' },
    { value: '02', label: 'Feb' },
    { value: '03', label: 'Mar' },
    { value: '04', label: 'Apr' },
    { value: '05', label: 'May' },
    { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' },
    { value: '08', label: 'Aug' },
    { value: '09', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' },
  ];
  readonly profitLossYearOptions = this.buildProfitLossYearOptions();

  readonly selectedProfitLossMonth = signal(this.currentMonthValue());
  readonly selectedProfitLossYear = signal(this.currentYearValue());
  readonly profitLossLoading = signal(true);
  readonly profitLossSourceRows = signal<ContractSupportRowApi[]>([]);
  readonly contracts = signal<Contract[]>([]);
  readonly loas = signal<Loa[]>([]);
  readonly loaSchedulesByLoa = signal<Record<string, LoaSchedule[]>>({});
  readonly loaBillConfigsByLoa = signal<Record<string, LoaBillConfig[]>>({});
  readonly vendors = signal<Vendor[]>([]);
  readonly applications = signal<ApplicationApi[]>([]);
  readonly generatedInvoices = signal<GeneratedInvoiceApi[]>([]);
  readonly calculationModal = signal<CalculationModalData | null>(null);

  readonly contractMap = computed(() => {
    const lookup = new Map<string, Contract>();
    for (const contract of this.contracts()) {
      const keys = [contract.id, contract.contract_number].filter((value): value is string => !!value);
      for (const key of keys) {
        if (!lookup.has(key)) lookup.set(key, contract);
      }
    }
    return lookup;
  });
  readonly loaMap = computed(() => new Map(this.loas().map((loa) => [loa.id, loa])));
  readonly vendorMap = computed(() => new Map(this.vendors().map((vendor) => [vendor.id, vendor])));
  readonly profitLossSupportRows = computed(() =>
    this.profitLossSourceRows().filter((row) => this.matchesProfitLossPeriod(row.period_from, row.period_to, this.selectedProfitLossMonth(), this.selectedProfitLossYear())),
  );
  readonly profitLossRows = computed<ProfitLossReportRow[]>(() => {
    const grouped = new Map<string, ContractSupportRowApi[]>();
    for (const row of this.profitLossSupportRows()) {
      const key = row.contract_number || row.contract_id;
      if (!key) continue;
      const existing = grouped.get(key);
      if (existing) existing.push(row);
      else grouped.set(key, [row]);
    }

    return Array.from(grouped.entries())
      .sort((left, right) => left[0].localeCompare(right[0]))
      .map(([contractKey, rows], index) => {
        const referenceRow = rows.find((row) => row.bess_role === 'Seller') || rows[0];
        const buyerSupportRow = this.resolveProfitLossSupportRow(rows, 'buy') || referenceRow;
        const sellerSupportRow = this.resolveProfitLossSupportRow(rows, 'sell') || referenceRow;
        const quantity = Math.max(...rows.map((row) => this.toNumber(row.quantum)), 0);
        const buyer = this.resolveContractPartyNameByKey(contractKey, 'buy') || referenceRow?.counterparty_vendor_name || referenceRow?.bess_vendor_name || 'NA';
        const seller = this.resolveContractPartyNameByKey(contractKey, 'sell') || referenceRow?.bess_vendor_name || referenceRow?.counterparty_vendor_name || 'NA';
        const buyerLoa = this.resolveContractPartyLoaNumberByKey(contractKey, 'buy', buyerSupportRow) || 'NA';
        const sellerLoa = this.resolveContractPartyLoaNumberByKey(contractKey, 'sell', sellerSupportRow) || 'NA';
        const buyCost = this.resolveContractPartyCostByKey(contractKey, 'buy', buyerSupportRow, quantity);
        const sellCost = this.resolveContractPartyCostByKey(contractKey, 'sell', sellerSupportRow, quantity);
        const chargeAllocation = this.computeChargeAllocationForContract(contractKey, buyer, seller);
        const charges = chargeAllocation.seciAmount;
        const totalRevenue = sellCost - buyCost;
        const billed = this.resolveBilledAmountForContract(contractKey, buyerSupportRow, sellerSupportRow, totalRevenue - charges);
        const clientName = rows.map((row) => row.counterparty_vendor_name).find((value) => !!value) || buyer || seller || 'NA';

        return {
          serialNo: index + 1,
          clientName,
          contractNumber: referenceRow?.contract_number || contractKey,
          buyer,
          seller,
          buyerLoa,
          sellerLoa,
          buyCost: Number(buyCost.toFixed(2)),
          sellCost: Number(sellCost.toFixed(2)),
          charges: Number(charges.toFixed(2)),
          totalRevenue: Number(totalRevenue.toFixed(2)),
          billed: Number(billed.toFixed(2)),
          netProfit: Number(billed.toFixed(2)),
        };
      });
  });

  private readonly api = inject(ApiService);
  private readonly contractsService = inject(ContractsService);
  private readonly loaService = inject(LoaService);
  private readonly vendorsService = inject(VendorsService);

  constructor() {
    this.loadProfitLossSupportRows();
    this.loadContracts();
    this.loadLoas();
    this.loadVendors();
    this.loadApplications();
    this.loadGeneratedInvoices();
  }

  setProfitLossMonth(value: string): void {
    this.selectedProfitLossMonth.set(this.normalizeProfitLossMonth(value));
  }

  setProfitLossYear(value: string): void {
    this.selectedProfitLossYear.set(String(value || this.currentYearValue()));
  }

  selectedProfitLossMonthLabel(): string {
    return this.profitLossMonthOptions.find((option) => option.value === this.selectedProfitLossMonth())?.label || this.selectedProfitLossMonth();
  }

  exportProfitLossReport(): void {
    const headers = ['S.No', 'Client Name', 'Contract', 'Buyer', 'Seller', 'Buyer LOA', 'Seller LOA', 'Buy Cost', 'Sell Cost', 'Charges', 'Total Revenue', 'Billed', 'Net Profit'];
    const lines = this.profitLossRows().map((row) => [
      row.serialNo,
      row.clientName,
      row.contractNumber,
      row.buyer,
      row.seller,
      row.buyerLoa,
      row.sellerLoa,
      row.buyCost,
      row.sellCost,
      row.charges,
      row.totalRevenue,
      row.billed,
      row.netProfit,
    ]);
    const csv = [headers, ...lines]
      .map((line) => line.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    if (typeof document === 'undefined') return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `profit-loss-${this.selectedProfitLossYear()}-${this.selectedProfitLossMonth()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  formatRupees(value: number): string {
    return `Rs${Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : '0'}`;
  }

  formatSignedRupees(value: number): string {
    const sign = value >= 0 ? '+' : '-';
    return `${sign}${this.formatRupees(Math.abs(value))}`;
  }

  openCostCalculationModal(row: ProfitLossReportRow, kind: 'buy' | 'sell'): void {
    this.calculationModal.set(this.buildCostCalculationModalData(row, kind));
  }

  openChargeCalculationModal(row: ProfitLossReportRow): void {
    this.calculationModal.set(this.buildChargeCalculationModalData(row));
  }

  closeCalculationModal(): void {
    this.calculationModal.set(null);
  }

  private resolveProfitLossSupportRow(rows: ContractSupportRowApi[], kind: 'buy' | 'sell'): ContractSupportRowApi | undefined {
    return rows.find((row) => this.normalizePartyKind(row.bess_role) === kind) || rows[0];
  }

  private buildCostCalculationModalData(row: ProfitLossReportRow, kind: 'buy' | 'sell'): CalculationModalData {
    const supportRows = this.findProfitLossSupportRowsForContract(row.contractNumber);
    const referenceRow = supportRows.find((item) => item.bess_role === 'Seller') || supportRows[0];
    const supportRow = this.resolveProfitLossSupportRow(supportRows, kind) || referenceRow;
    const contractKey = referenceRow?.contract_number || referenceRow?.contract_id || row.contractNumber;
    const loaId = supportRow ? this.resolveContractPartyLoaIdByKey(contractKey, kind, supportRow) : '';
    const loaNumber = supportRow ? this.resolveContractPartyLoaNumberByKey(contractKey, kind, supportRow) || 'NA' : 'NA';
    const fallbackQuantity = Math.max(...supportRows.map((item) => this.toNumber(item.quantum)), 0);
    const quantity = supportRow ? this.resolveContractPartyQuantityByKey(contractKey, kind, supportRow, fallbackQuantity) : 0;
    const loa = loaId ? this.loaMap().get(loaId) : null;
    const loaRate = this.toNumber(loa?.fixed_rate ?? loa?.mcp_value ?? supportRow?.loa_fixed_rate ?? supportRow?.loa_mcp_value);
    const rate = supportRow ? this.resolveContractPartyRateByKey(contractKey, kind, supportRow) : 0;
    const scheduledMwh = loaId ? this.monthlyScheduledMwhForLoa(loaId, this.selectedProfitLossMonth(), this.selectedProfitLossYear()) : 0;
    const usesSchedule = scheduledMwh > 0 && rate > 0;
    const billedQuantity = usesSchedule ? scheduledMwh : quantity;
    const finalCost = usesSchedule ? scheduledMwh * rate * 1000 : quantity * rate * 1000;
    const rateSource = loaRate > 0
      ? `LOA rate from ${loaNumber}`
      : 'Fallback contract rate (agreement, contract type, category, quantity, and side)';
    const quantityLabel = usesSchedule ? 'Scheduled Energy' : 'Contract Quantity';
    const quantityValue = usesSchedule ? `${this.formatNumber(scheduledMwh)} MWh` : `${this.formatNumber(quantity)} MW`;
    const subtitleParty = kind === 'buy' ? row.buyer : row.seller;

    return {
      title: `${kind === 'buy' ? 'Buy Cost' : 'Sell Cost'} Calculation`,
      subtitle: `${row.contractNumber} • ${subtitleParty}`,
      equation: `${this.formatRupees(finalCost)} = ${this.formatNumber(billedQuantity)} x ${this.formatNumber(rate)} x 1000`,
      hint: usesSchedule
        ? 'This cost uses LOA schedule energy for the selected month because schedule data is available.'
        : 'This cost uses contract quantity because no monthly LOA schedule energy was available.',
      details: [
        { label: 'Client', value: row.clientName },
        { label: 'Contract', value: row.contractNumber },
        { label: 'Party', value: subtitleParty },
        { label: 'LOA', value: loaNumber },
        { label: 'Rate Source', value: rateSource },
        { label: quantityLabel, value: quantityValue },
        { label: 'Rate', value: `${this.formatNumber(rate)}/unit` },
        { label: 'Computed Cost', value: this.formatRupees(finalCost) },
      ],
    };
  }

  private buildChargeCalculationModalData(row: ProfitLossReportRow): CalculationModalData {
    const allocation = this.computeChargeAllocationForContract(row.contractNumber, row.buyer, row.seller);
    const detailRows: Array<{ label: string; value: string }> = [
      { label: 'Client', value: row.clientName },
      { label: 'Contract', value: row.contractNumber },
      { label: 'Buyer', value: row.buyer },
      { label: 'Seller', value: row.seller },
      { label: 'Total Approval Charges', value: this.formatRupees(allocation.totalCharges) },
      { label: 'Buyer Share', value: this.formatRupees(allocation.buyerAmount) },
      { label: 'Seller Share', value: this.formatRupees(allocation.sellerAmount) },
      { label: 'SECI Counted Charges', value: this.formatRupees(allocation.seciAmount) },
    ];

    for (const item of allocation.rows) {
      detailRows.push({
        label: `${item.applicationNumber} • ${item.chargeType}`,
        value: `${this.formatRupees(item.totalAmount)} | Buyer ${this.formatRupees(item.buyerAmount)} | Seller ${this.formatRupees(item.sellerAmount)} | SECI ${this.formatRupees(item.seciAmount)} | ${item.allocationLabel}`,
      });
    }

    return {
      title: 'Charges Calculation',
      subtitle: `${row.contractNumber} • Punched application approval charges`,
      equation: `${this.formatRupees(allocation.seciAmount)} = SECI share of punched approval charges`,
      hint: allocation.rows.length
        ? 'If LOA OA charge allocation has buyer and seller percentages totaling 100, charges are bifurcated. Otherwise the full charge is counted on the SECI side.'
        : 'No punched application approval charges were found for this contract in the selected month.',
      details: detailRows,
    };
  }

  private resolveBilledAmountForContract(
    contractKey: string,
    buyerSupportRow: ContractSupportRowApi | undefined,
    sellerSupportRow: ContractSupportRowApi | undefined,
    fallbackBilled: number,
  ): number {
    const loaIds = [
      this.resolveContractPartyLoaIdByKey(contractKey, 'buy', buyerSupportRow),
      this.resolveContractPartyLoaIdByKey(contractKey, 'sell', sellerSupportRow),
      String(buyerSupportRow?.loa_id || '').trim(),
      String(sellerSupportRow?.loa_id || '').trim(),
    ].filter((value): value is string => !!value);
    if (!loaIds.length) return 0;

    const matchingInvoices = this.generatedInvoices().filter((invoice) => {
      if ((String(invoice.invoice_type || invoice.mode || '').toUpperCase()) !== 'OA') return false;
      const row = invoice.row_json || {};
      const applicationId = String(row['application_id'] || '').trim();
      if (!applicationId) return false;
      const app = this.findApplicationByIdentifier(applicationId);
      if (!app || !this.matchesGeneratedInvoiceMonth(invoice, row)) return false;
      const party = String(row['party'] || '').trim().toUpperCase();
      const invoiceLoaId = party === 'BUYER' ? String(app.buyer_loa_id || '').trim() : String(app.seller_loa_id || '').trim();
      return !!invoiceLoaId && loaIds.includes(invoiceLoaId);
    });

    if (!matchingInvoices.length) return 0;
    const total = matchingInvoices.reduce((sum, invoice) => sum + this.toNumber(invoice.row_json?.['total_amount'] as string | number | null | undefined), 0);
    return Number(total.toFixed(2));
  }

  private findApplicationByIdentifier(identifier: string): ApplicationApi | undefined {
    return this.applications().find(
      (app) => String(app.id || '').trim() === identifier || String(app.application_number || '').trim() === identifier,
    );
  }

  private matchesGeneratedInvoiceMonth(invoice: GeneratedInvoiceApi, row: Record<string, unknown>): boolean {
    const fromDate = String(row['from_date'] || '').trim();
    const toDate = String(row['to_date'] || '').trim();
    if (fromDate || toDate) {
      return this.matchesProfitLossPeriod(fromDate || toDate, toDate || fromDate, this.selectedProfitLossMonth(), this.selectedProfitLossYear());
    }
    const invoiceDate = this.parseDateOnly(String(invoice.invoice_date || '').trim());
    if (!invoiceDate) return false;
    return invoiceDate.getFullYear() === Number(this.selectedProfitLossYear()) && invoiceDate.getMonth() + 1 === Number(this.selectedProfitLossMonth());
  }

  private computeChargeAllocationForContract(contractKey: string, buyerName: string, sellerName: string): ChargeAllocationResult {
    const contract = this.contractMap().get(contractKey);
    const contractId = String(contract?.id || contractKey).trim();
    const applications = this.applications().filter((app) => {
      const status = String(app.status || '').trim().toLowerCase();
      if (status !== 'punched') return false;
      if (String(app.contract_id || '').trim() !== contractId) return false;
      return this.matchesApplicationMonth(app);
    });

    const seciSide = this.resolveSeciSide(buyerName, sellerName);
    const rows: ChargeAllocationResult['rows'] = [];
    let totalCharges = 0;
    let buyerAmount = 0;
    let sellerAmount = 0;
    let seciAmount = 0;

    for (const app of applications) {
      const allocations = this.parseOaAllocation(this.latestOaConfigValue(String(app.seller_loa_id || '')), app.approval_charges || []);
      for (const charge of allocations) {
        const totalAmount = charge.totalAmount;
        const validSplit = this.isValidAllocationSplit(charge);
        const buyerShare = validSplit ? (totalAmount * charge.buyerPercent) / 100 : 0;
        const sellerShare = validSplit ? (totalAmount * charge.sellerPercent) / 100 : 0;
        const seciShare = validSplit ? (seciSide === 'buyer' ? buyerShare : sellerShare) : totalAmount;
        totalCharges += totalAmount;
        buyerAmount += buyerShare;
        sellerAmount += sellerShare;
        seciAmount += seciShare;
        rows.push({
          applicationNumber: app.application_number || String(app.id),
          chargeType: charge.chargeType,
          totalAmount,
          buyerAmount: buyerShare,
          sellerAmount: sellerShare,
          seciAmount: seciShare,
          allocationLabel: validSplit
            ? `Buyer ${this.formatNumber(charge.buyerPercent)}% / Seller ${this.formatNumber(charge.sellerPercent)}%`
            : 'No valid 100% split on LOA, so full charge counted on SECI',
        });
      }
    }

    return {
      totalCharges: Number(totalCharges.toFixed(2)),
      seciAmount: Number(seciAmount.toFixed(2)),
      buyerAmount: Number(buyerAmount.toFixed(2)),
      sellerAmount: Number(sellerAmount.toFixed(2)),
      seciSide,
      rows,
    };
  }

  private matchesApplicationMonth(app: ApplicationApi): boolean {
    const scheduleRows = app.approval_schedules || [];
    if (scheduleRows.length) {
      return scheduleRows.some((row) => {
        const from = row.schedule_from || app.application_date;
        const to = row.schedule_to || row.schedule_from || app.application_date;
        return this.matchesProfitLossPeriod(from, to, this.selectedProfitLossMonth(), this.selectedProfitLossYear());
      });
    }
    const applicationDate = this.parseDateOnly(app.application_date);
    if (!applicationDate) return false;
    return applicationDate.getFullYear() === Number(this.selectedProfitLossYear()) && applicationDate.getMonth() + 1 === Number(this.selectedProfitLossMonth());
  }

  private resolveSeciSide(buyerName: string, sellerName: string): 'buyer' | 'seller' {
    const buyer = String(buyerName || '').toUpperCase();
    const seller = String(sellerName || '').toUpperCase();
    if (seller.includes('SECI')) return 'seller';
    if (buyer.includes('SECI')) return 'buyer';
    return 'seller';
  }

  private latestOaConfigValue(loaId: string): string | null {
    const latest = this.latestOaConfig(this.loaBillConfigsByLoa()[loaId] || []);
    return latest?.oa_charge_allocation || null;
  }

  private latestOaConfig(configs: LoaBillConfig[]): LoaBillConfig | null {
    const oaConfigs = configs.filter((item) => String(item.bill_type || '').toUpperCase() === 'OA');
    if (!oaConfigs.length) return null;
    return [...oaConfigs].sort((left, right) => String(right.effective_date || '').localeCompare(String(left.effective_date || '')))[0] || null;
  }

  private parseOaAllocation(raw: string | null, approvalCharges: ApplicationChargeApi[]): Array<OaChargeAllocationRow & { totalAmount: number }> {
    const chargeTotals = this.resolveApprovalChargeTotals(approvalCharges);
    const defaults = chargeTotals.map((item) => ({
      chargeType: item.chargeType,
      totalAmount: item.totalAmount,
      paidBy: 'Both' as const,
      buyerPercent: 50,
      sellerPercent: 50,
    }));

    if (!raw || !raw.trim()) return defaults.map((item) => ({ ...item, buyerPercent: 0, sellerPercent: 0 }));

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return defaults.map((item) => ({ ...item, buyerPercent: 0, sellerPercent: 0 }));
      const byType = new Map<string, OaChargeAllocationRow>();

      for (const row of parsed) {
        if (!row || typeof row !== 'object') continue;
        const source = row as Record<string, unknown>;
        const chargeType = String(source['charge_type'] || '').trim();
        if (!chargeType) continue;

        const payer = String(source['payer'] || 'Shared');
        const paidBy: OaChargeAllocationRow['paidBy'] = payer === 'Buyer' ? 'Buyer' : payer === 'Seller' ? 'Seller' : 'Both';
        const buyerPercent = Number(source['buyer_percent']);
        const sellerPercent = Number(source['seller_percent']);

        byType.set(chargeType, {
          chargeType,
          paidBy,
          buyerPercent: Number.isFinite(buyerPercent) ? buyerPercent : paidBy === 'Buyer' ? 100 : paidBy === 'Seller' ? 0 : 50,
          sellerPercent: Number.isFinite(sellerPercent) ? sellerPercent : paidBy === 'Seller' ? 100 : paidBy === 'Buyer' ? 0 : 50,
        });
      }

      return chargeTotals.map((item) => {
        const matched = byType.get(item.chargeType);
        return {
          chargeType: item.chargeType,
          totalAmount: item.totalAmount,
          paidBy: matched?.paidBy || 'Both',
          buyerPercent: matched?.buyerPercent ?? 0,
          sellerPercent: matched?.sellerPercent ?? 0,
        };
      });
    } catch {
      return defaults.map((item) => ({ ...item, buyerPercent: 0, sellerPercent: 0 }));
    }
  }

  private isValidAllocationSplit(charge: { buyerPercent: number; sellerPercent: number }): boolean {
    const total = this.toNumber(charge.buyerPercent) + this.toNumber(charge.sellerPercent);
    return Math.abs(total - 100) < 0.001;
  }

  private resolveApprovalChargeTotals(charges: ApplicationChargeApi[]): Array<{ chargeType: string; totalAmount: number }> {
    if (!charges.length) return [];
    return charges.map((item, index) => ({
      chargeType: this.normalizeChargeType(item, index),
      totalAmount: Number(item.amount) || (Number(item.rate_mwh) || 0) * (Number(item.quantum_mwh) || 0),
    }));
  }

  private normalizeChargeType(item: ApplicationChargeApi, index: number): string {
    const raw = `${item.sub_group_name || ''} ${item.group_name || ''} ${item.section_heading || ''}`.trim();
    if (!raw) return `Charge ${index + 1}`;
    const normalized = raw.toUpperCase();
    if (normalized.includes('TRANSMISSION')) return 'Transmission';
    if (normalized.includes('WHEEL')) return 'Wheeling';
    if (normalized.includes('SLDC')) return 'SLDC';
    if (normalized.includes('RLDC')) return 'RLDC';
    if (normalized.includes('LOSS')) return 'Loss';
    if (normalized.includes('APP')) return 'App';
    return raw;
  }

  private findProfitLossSupportRowsForContract(contractIdentifier: string): ContractSupportRowApi[] {
    return this.profitLossSupportRows().filter(
      (row) => row.contract_number === contractIdentifier || row.contract_id === contractIdentifier,
    );
  }

  private formatNumber(value: number): string {
    if (!Number.isFinite(value)) return '0';
    return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  }

  private matchesProfitLossPeriod(periodFrom: string, periodTo: string, month: string, year: string): boolean {
    const start = new Date(`${year}-${month}-01T00:00:00`);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    const from = new Date(periodFrom);
    const to = new Date(periodTo);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      return false;
    }
    return from <= end && to >= start;
  }

  private resolveContractPartyByKey(contractKey: string, kind: 'buy' | 'sell') {
    const contract = this.contractMap().get(contractKey);
    const parties = contract?.party_details || [];
    const resolvedKinds = parties.map((party) => this.resolveContractPartyKind(party));
    const exactMatchIndex = resolvedKinds.findIndex((partyKind) => partyKind === kind);
    let match = exactMatchIndex >= 0 ? parties[exactMatchIndex] : undefined;

    if (!match && parties.length === 2) {
      const oppositeMatchIndex = resolvedKinds.findIndex((partyKind) => !!partyKind && partyKind !== kind);
      if (oppositeMatchIndex >= 0) {
        match = parties[oppositeMatchIndex === 0 ? 1 : 0];
      }
    }

    if (!match) {
      match = kind === 'buy' ? parties[1] || parties[0] : parties[0];
    }
    return match;
  }

  private resolveContractPartyKind(party: { party_type?: string | null; loa_id?: string | null } | undefined): 'buy' | 'sell' | null {
    const explicitPartyKind = this.normalizePartyKind(party?.party_type);
    if (explicitPartyKind) return explicitPartyKind;
    const loaId = String(party?.loa_id || '').trim();
    if (!loaId) return null;
    return this.normalizePartyKind(this.loaMap().get(loaId)?.category);
  }

  private normalizePartyKind(value: string | null | undefined): 'buy' | 'sell' | null {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return null;
    if (normalized.includes('buy') || normalized.includes('purchase')) return 'buy';
    if (normalized.includes('sell') || normalized.includes('sale')) return 'sell';
    return null;
  }

  private resolveContractPartyNameByKey(contractKey: string, kind: 'buy' | 'sell'): string {
    const match = this.resolveContractPartyByKey(contractKey, kind);
    const loaId = String(match?.loa_id || '').trim();
    if (loaId) {
      const loa = this.loaMap().get(loaId);
      const loaVendorId = String(loa?.vendor_id || '').trim();
      const linkedVendorName = loaVendorId ? this.vendorMap().get(loaVendorId)?.vendor_name || '' : '';
      if (linkedVendorName) return linkedVendorName;
      if (loa?.loa_number) return loa.loa_number;
    }
    const vendorId = String(match?.vendor_id || '').trim();
    return vendorId ? this.vendorMap().get(vendorId)?.vendor_name || '' : '';
  }

  private resolveContractPartyLoaNumberByKey(contractKey: string, kind: 'buy' | 'sell', supportRow?: ContractSupportRowApi): string {
    const match = this.resolveContractPartyByKey(contractKey, kind);
    const loaId = String(match?.loa_id || supportRow?.loa_id || '').trim();
    return loaId ? this.loaMap().get(loaId)?.loa_number || '' : '';
  }

  private resolveContractPartyLoaIdByKey(contractKey: string, kind: 'buy' | 'sell', supportRow?: ContractSupportRowApi): string {
    const match = this.resolveContractPartyByKey(contractKey, kind);
    return String(match?.loa_id || supportRow?.loa_id || '').trim();
  }

  private resolveContractPartyQuantityByKey(contractKey: string, kind: 'buy' | 'sell', supportRow: ContractSupportRowApi | undefined, fallbackQuantity: number): number {
    const match = this.resolveContractPartyByKey(contractKey, kind);
    const partyQuantity = this.toNumber(match?.quantum);
    if (partyQuantity > 0) return partyQuantity;
    const supportQuantity = this.toNumber(supportRow?.quantum);
    if (supportQuantity > 0) return supportQuantity;
    return fallbackQuantity;
  }

  private resolveContractPartyRateByKey(contractKey: string, kind: 'buy' | 'sell', supportRow: ContractSupportRowApi): number {
    const match = this.resolveContractPartyByKey(contractKey, kind);
    const loaId = String(match?.loa_id || supportRow?.loa_id || '').trim();
    const loa = loaId ? this.loaMap().get(loaId) : null;
    const loaRate = this.toNumber(loa?.fixed_rate ?? loa?.mcp_value ?? supportRow.loa_fixed_rate ?? supportRow.loa_mcp_value);
    if (loaRate > 0) return loaRate;
    return this.deriveFallbackContractRate(supportRow, kind);
  }

  private resolveContractPartyCostByKey(contractKey: string, kind: 'buy' | 'sell', supportRow: ContractSupportRowApi, fallbackQuantity: number): number {
    const loaId = this.resolveContractPartyLoaIdByKey(contractKey, kind, supportRow);
    const rate = this.resolveContractPartyRateByKey(contractKey, kind, supportRow);
    const scheduledMwh = loaId ? this.monthlyScheduledMwhForLoa(loaId, this.selectedProfitLossMonth(), this.selectedProfitLossYear()) : 0;
    if (scheduledMwh > 0 && rate > 0) {
      return scheduledMwh * rate * 1000;
    }

    const quantity = this.resolveContractPartyQuantityByKey(contractKey, kind, supportRow, fallbackQuantity);
    return quantity * rate * 1000;
  }

  private deriveFallbackContractRate(supportRow: ContractSupportRowApi, kind: 'buy' | 'sell'): number {
    const quantum = this.toNumber(supportRow.quantum);
    const agreement = String(supportRow.agreement_type || '').toUpperCase();
    const contractType = String(supportRow.contract_type || '').toUpperCase();
    const category = String(supportRow.contract_category || '').toUpperCase();
    const agreementOffset = agreement.includes('GREEN') ? 0.35 : agreement.includes('BANK') ? 0.2 : 0;
    const roleOffset = kind === 'sell' ? 0.15 : -0.05;
    const scaleOffset = Math.min(0.8, quantum / 250);
    const tenorOffset = contractType.includes('LTOA') ? 0.28 : contractType.includes('MTOA') ? 0.18 : 0.08;
    const categoryOffset = category.includes('PEAK') ? 0.25 : category.includes('RTC') ? 0.18 : 0;
    return Number((3.15 + agreementOffset + roleOffset + scaleOffset + tenorOffset + categoryOffset).toFixed(2));
  }

  private monthlyScheduledMwhForLoa(loaId: string, month: string, year: string): number {
    const schedules = this.loaSchedulesByLoa()[loaId] || [];
    if (!schedules.length) return 0;

    const monthStart = new Date(`${year}-${month}-01T00:00:00`);
    const monthEnd = new Date(Number(year), Number(month), 0, 23, 59, 59, 999);
    if (Number.isNaN(monthStart.getTime()) || Number.isNaN(monthEnd.getTime())) return 0;

    let total = 0;
    for (const schedule of schedules) {
      total += this.scheduledMwhWithinPeriod(schedule, monthStart, monthEnd);
    }
    return total;
  }

  private scheduledMwhWithinPeriod(schedule: LoaSchedule, periodStart: Date, periodEnd: Date): number {
    const scheduleStart = this.parseDateOnly(schedule.from_date);
    const scheduleEnd = this.parseDateOnly(schedule.to_date);
    const quantumMw = this.toNumber(schedule.quantum_mw);
    if (!scheduleStart || !scheduleEnd || quantumMw <= 0) return 0;

    const overlapStart = scheduleStart > periodStart ? scheduleStart : periodStart;
    const overlapEnd = scheduleEnd < periodEnd ? scheduleEnd : periodEnd;
    if (overlapStart > overlapEnd) return 0;

    let total = 0;
    for (let day = new Date(overlapStart.getFullYear(), overlapStart.getMonth(), overlapStart.getDate()); day <= overlapEnd; day.setDate(day.getDate() + 1)) {
      total += this.dailyScheduleMwh(quantumMw, schedule.from_time, schedule.to_time);
    }
    return total;
  }

  private dailyScheduleMwh(quantumMw: number, fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const hours = this.scheduleHours(fromTime, toTime);
    return quantumMw * hours;
  }

  private scheduleHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const start = this.parseTimeToMinutes(fromTime);
    const end = this.parseTimeToMinutes(toTime);
    if (start === null || end === null) return 24;
    if (end <= start) return (24 * 60 - start + end) / 60;
    return (end - start) / 60;
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    const parsed = new Date(`${String(value || '').trim()}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    const raw = String(value || '').trim();
    if (!raw) return null;
    if (raw === '24:00' || raw === '24:00:00' || raw === '2400') return 24 * 60;
    const parts = raw.split(':');
    const hour = Number(parts[0]);
    const minute = Number(parts[1] || '0');
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return hour * 60 + minute;
  }

  private normalizeProfitLossMonth(value: string): string {
    const normalized = String(value || '').padStart(2, '0');
    return this.profitLossMonthOptions.some((option) => option.value === normalized) ? normalized : this.currentMonthValue();
  }

  private currentMonthValue(): string {
    return String(new Date().getMonth() + 1).padStart(2, '0');
  }

  private currentYearValue(): string {
    return String(new Date().getFullYear());
  }

  private buildProfitLossYearOptions(): string[] {
    const currentYear = Number(this.currentYearValue());
    return Array.from({ length: 7 }, (_, index) => String(currentYear - 3 + index));
  }

  private toNumber(value: string | number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private loadProfitLossSupportRows(): void {
    this.api.get<ContractSupportRowApi[]>('/api/contracts-support/all-vendors').subscribe({
      next: (rows) => {
        this.profitLossSourceRows.set(rows || []);
        this.profitLossLoading.set(false);
      },
      error: () => {
        this.profitLossSourceRows.set([]);
        this.profitLossLoading.set(false);
      },
    });
  }

  private loadApplications(): void {
    this.api.get<ApplicationApi[]>('/api/applications').subscribe({
      next: (rows) => this.applications.set(rows || []),
      error: () => this.applications.set([]),
    });
  }

  private loadGeneratedInvoices(): void {
    this.api.get<GeneratedInvoiceApi[]>('/api/invoices/generated').subscribe({
      next: (rows) => this.generatedInvoices.set(rows || []),
      error: () => this.generatedInvoices.set([]),
    });
  }

  private loadContracts(): void {
    this.contractsService.list().subscribe({
      next: (rows) => this.contracts.set(rows || []),
      error: () => this.contracts.set([]),
    });
  }

  private loadLoas(): void {
    this.loaService.list().subscribe({
      next: (rows) => {
        const loaRows = rows || [];
        this.loas.set(loaRows);
        this.loadLoaSchedules(loaRows);
        this.loadLoaBillConfigs(loaRows);
      },
      error: () => {
        this.loas.set([]);
        this.loaSchedulesByLoa.set({});
        this.loaBillConfigsByLoa.set({});
      },
    });
  }

  private loadLoaSchedules(rows: Loa[]): void {
    const lookup: Record<string, LoaSchedule[]> = {};
    if (!rows.length) {
      this.loaSchedulesByLoa.set(lookup);
      return;
    }

    this.loaSchedulesByLoa.set(lookup);
    rows.forEach((loa) => {
      const loaId = String(loa.id || '').trim();
      if (!loaId) return;
      this.loaService.listSchedules(loaId).subscribe({
        next: (schedules) => {
          this.loaSchedulesByLoa.update((current) => ({ ...current, [loaId]: schedules || [] }));
        },
        error: () => {
          this.loaSchedulesByLoa.update((current) => ({ ...current, [loaId]: [] }));
        },
      });
    });
  }

  private loadLoaBillConfigs(rows: Loa[]): void {
    const lookup: Record<string, LoaBillConfig[]> = {};
    if (!rows.length) {
      this.loaBillConfigsByLoa.set(lookup);
      return;
    }

    this.loaBillConfigsByLoa.set(lookup);
    rows.forEach((loa) => {
      const loaId = String(loa.id || '').trim();
      if (!loaId) return;
      this.loaService.listBillConfigs(loaId).subscribe({
        next: (configs) => {
          this.loaBillConfigsByLoa.update((current) => ({ ...current, [loaId]: configs || [] }));
        },
        error: () => {
          this.loaBillConfigsByLoa.update((current) => ({ ...current, [loaId]: [] }));
        },
      });
    });
  }

  private loadVendors(): void {
    this.vendorsService.list().subscribe({
      next: (rows) => this.vendors.set(rows || []),
      error: () => this.vendors.set([]),
    });
  }
}
